import React, { useState, useEffect } from 'react';
import { TileCard, EmpButton as Button } from '@empowernow/ui';

export function FormModalSkeleton({ onClose }: { onClose: () => void }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: isVisible ? 'blur(4px)' : 'blur(0px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      <style>{`
        @keyframes pulse {
          0% { opacity: 0.4; }
          100% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
      
      <div style={{
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: isVisible ? 'modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
      }}>
        <TileCard theme="auto" elevation={2}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--card-header-bg)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ flex: 1 }}>
              {/* Title skeleton with icon */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: 'linear-gradient(90deg, rgba(45, 55, 75, 0.4) 0%, rgba(45, 55, 75, 0.6) 50%, rgba(45, 55, 75, 0.4) 100%)',
                  backgroundSize: '1000px 100%',
                  borderRadius: '4px',
                  animation: 'shimmer 2s infinite linear'
                }} />
                <div style={{
                  height: '24px',
                  background: 'linear-gradient(90deg, rgba(45, 55, 75, 0.4) 0%, rgba(45, 55, 75, 0.6) 50%, rgba(45, 55, 75, 0.4) 100%)',
                  backgroundSize: '1000px 100%',
                  borderRadius: '4px',
                  width: '50%',
                  animation: 'shimmer 2s infinite linear'
                }} />
              </div>
              {/* Description skeleton */}
              <div style={{
                height: '14px',
                background: 'linear-gradient(90deg, var(--disabled-bg) 0%, rgba(45, 55, 75, 0.4) 50%, var(--disabled-bg) 100%)',
                backgroundSize: '1000px 100%',
                borderRadius: '4px',
                width: '75%',
                marginLeft: '32px',
                animation: 'shimmer 2s infinite linear'
              }} />
            </div>
            <Button
              onClick={onClose}
              style={{ 
                background: 'transparent',
                border: 'none',
                color: 'var(--main-text-secondary)',
                padding: '8px',
                fontSize: '24px',
                cursor: 'pointer',
                minWidth: '40px',
                minHeight: '40px'
              }}
            >
              ✕
            </Button>
          </div>

          {/* Form field skeletons */}
          <div style={{ padding: '24px' }}>
            {Array.from({ length: 5 }).map((_, idx) => (
              <div key={idx} style={{ marginBottom: '16px' }}>
                {/* Label skeleton */}
                <div style={{
                  height: '14px',
                  background: 'linear-gradient(90deg, var(--disabled-bg) 0%, rgba(45, 55, 75, 0.4) 50%, var(--disabled-bg) 100%)',
                  backgroundSize: '1000px 100%',
                  borderRadius: '4px',
                  marginBottom: '6px',
                  width: `${25 + (idx * 5)}%`,
                  animation: 'shimmer 2s infinite linear',
                  animationDelay: `${idx * 0.1}s`
                }} />
                {/* Input skeleton */}
                <div style={{
                  height: '40px',
                  background: 'linear-gradient(90deg, rgba(45, 55, 75, 0.3) 0%, rgba(45, 55, 75, 0.5) 50%, rgba(45, 55, 75, 0.3) 100%)',
                  backgroundSize: '1000px 100%',
                  borderRadius: '4px',
                  width: '100%',
                  border: '1px solid var(--border-color)',
                  animation: 'shimmer 2s infinite linear',
                  animationDelay: `${idx * 0.1}s`
                }} />
              </div>
            ))}
            
            {/* Action buttons skeleton */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid var(--border-color)'
            }}>
              <div style={{
                height: '40px',
                width: '100px',
                background: 'linear-gradient(90deg, rgba(45, 55, 75, 0.4) 0%, rgba(45, 55, 75, 0.6) 50%, rgba(45, 55, 75, 0.4) 100%)',
                backgroundSize: '1000px 100%',
                borderRadius: '4px',
                animation: 'shimmer 2s infinite linear'
              }} />
              <div style={{
                height: '40px',
                width: '120px',
                background: 'linear-gradient(90deg, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0.6) 50%, rgba(139, 92, 246, 0.4) 100%)',
                backgroundSize: '1000px 100%',
                borderRadius: '4px',
                animation: 'shimmer 2s infinite linear'
              }} />
            </div>
          </div>
        </TileCard>
      </div>
    </div>
  );
}
